import { Controller } from "@hotwired/stimulus"

// Formats US phone numbers as user types or pastes
// Usage: <input type="tel" data-controller="phone-format" data-action="input->phone-format#format">
//
// Formats to: (555) 123-4567
// Handles various input formats: 5551234567, +15551234567, 1-555-123-4567, etc.

export default class extends Controller {
  connect() {
    // Ensure mobile devices show the phone keypad
    this.element.setAttribute("inputmode", "tel")
    this.element.setAttribute("type", "tel")

    // Format any existing value on connect
    this.format()

    // Add paste handler for immediate formatting
    this.element.addEventListener("paste", this.handlePaste.bind(this))
  }

  disconnect() {
    this.element.removeEventListener("paste", this.handlePaste.bind(this))
  }

  handlePaste(event) {
    // Let the paste happen, then format after a tiny delay
    setTimeout(() => this.format(), 0)
  }

  format() {
    const input = this.element
    const cursorPosition = input.selectionStart
    const oldLength = input.value.length
    const oldValue = input.value

    // Get just the digits
    let digits = input.value.replace(/\D/g, "")

    // Remove leading 1 if present (allow people to paste +1 numbers)
    if (digits.length > 10 && digits.startsWith("1")) {
      digits = digits.substring(1)
    }

    // Format based on digit count
    const formatted = this.formatUSPhone(digits)

    // Only update if value changed (prevents cursor jumping)
    if (input.value !== formatted) {
      input.value = formatted

      // Calculate new cursor position
      // Count how many digits are before cursor in old value
      const digitsBeforeCursor = oldValue.substring(0, cursorPosition).replace(/\D/g, "").length

      // Find position in new value that has same number of digits before it
      let newPosition = 0
      let digitCount = 0
      for (let i = 0; i < formatted.length && digitCount < digitsBeforeCursor; i++) {
        newPosition = i + 1
        if (/\d/.test(formatted[i])) {
          digitCount++
        }
      }

      // Only set selection if element is focused
      if (document.activeElement === input) {
        input.setSelectionRange(newPosition, newPosition)
      }
    }
  }

  formatUSPhone(digits) {
    // Limit to 10 digits
    digits = digits.substring(0, 10)

    if (digits.length === 0) {
      return ""
    } else if (digits.length <= 3) {
      return `(${digits}`
    } else if (digits.length <= 6) {
      return `(${digits.substring(0, 3)}) ${digits.substring(3)}`
    } else {
      return `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6)}`
    }
  }
}
